import type { FC, ReactNode } from 'react';
import { useState } from 'react';

import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import { CheckIcon, CloseIcon } from '@truvhq/ui';
import { clsx } from 'clsx';
import DOMPurify from 'dompurify';
import { match, P } from 'ts-pattern';

import { formatNotificationDate } from 'shared/lib/datetime/formatNotificationDate';

import MarkAsUnreadIcon from './icons/mark-as-unread.svg?react';

type FeedItemLike = {
  id: string;
  inserted_at: string;
  read_at: string | null;
};

type NotificationItemProps = {
  item: FeedItemLike;
  icon: ReactNode;
  renderedContent: string | null;
  isUnread: boolean;
  onClick?: () => void;
  onClose?: () => void;
  onMarkAsRead?: (e: React.MouseEvent) => void;
  onMarkAsUnread?: (e: React.MouseEvent) => void;
  showUnreadToggle?: boolean;
  showDotIndicator?: boolean;
  className?: string;
};

export const NotificationItem: FC<NotificationItemProps> = ({
  item,
  icon,
  renderedContent,
  isUnread,
  onClick,
  onClose,
  onMarkAsRead,
  onMarkAsUnread,
  showUnreadToggle = false,
  showDotIndicator = true,
  className,
}) => {
  const [isMarkAsReadTooltipOpen, setIsMarkAsReadTooltipOpen] = useState(false);
  const [isMarkAsUnreadTooltipOpen, setIsMarkAsUnreadTooltipOpen] = useState(false);

  return (
    <Box
      className={clsx(
        'group relative flex min-h-[68px] items-center gap-3 rounded-2xl border border-grey30 bg-white p-4 transition-colors',
        onClick && 'cursor-pointer hover:border-grey40',
        className,
      )}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onClick={onClick}
      onKeyDown={
        onClick
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onClick();
              }
            }
          : undefined
      }
      aria-label={onClick ? 'View notification' : undefined}
    >
      {onClose && (
        <IconButton
          onClick={(e) => {
            e.stopPropagation();
            onClose();
          }}
          aria-label="Close"
          className="absolute -top-2 -left-2 h-6 w-6 p-0 rounded-full opacity-0 transition-all duration-200 group-hover:opacity-100"
          sx={{
            border: '1px solid',
            borderColor: 'grey.300',
            backgroundColor: 'white',
            '&:hover': {
              backgroundColor: 'grey.50',
            },
          }}
        >
          <CloseIcon width={16} height={16} className="text-grey60" />
        </IconButton>
      )}

      <Box className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-grey20">{icon}</Box>

      <Box className="min-w-0 flex-1">
        {renderedContent && (
          <Typography
            className="text-sm leading-snug [&_a]:text-primary-main [&_a]:underline"
            // biome-ignore lint/security/noDangerouslySetInnerHtml: content sanitized with DOMPurify
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(renderedContent, {
                ALLOWED_TAGS: ['p', 'a', 'strong', 'em', 'br'],
                ALLOWED_ATTR: ['href', 'target', 'rel'],
              }),
            }}
          />
        )}
      </Box>

      {match({ showDotIndicator, isUnread, onMarkAsRead, onMarkAsUnread, showUnreadToggle })
        .with({ showDotIndicator: true }, () => (
          <>
            <Typography className="w-16 shrink-0 text-left text-xs text-grey60">
              {formatNotificationDate(new Date(item.inserted_at))}
            </Typography>

            <Box className="flex w-4 shrink-0 items-center justify-center">
              <Box
                className={clsx('h-2 w-2 rounded-full group-hover:hidden', isUnread ? 'bg-primary-main' : 'bg-grey20')}
              />
              {match({ isUnread, onMarkAsRead, onMarkAsUnread, showUnreadToggle })
                .with({ isUnread: true, onMarkAsRead: P.not(P.nullish) }, ({ onMarkAsRead }) => (
                  <Tooltip
                    title="Mark as read"
                    placement="top"
                    open={isMarkAsReadTooltipOpen}
                    onOpen={() => setIsMarkAsReadTooltipOpen(true)}
                    onClose={() => setIsMarkAsReadTooltipOpen(false)}
                    slotProps={{ popper: { modifiers: [{ name: 'offset', options: { offset: [-32, -8] } }] } }}
                  >
                    <IconButton
                      size="small"
                      className={clsx(isMarkAsReadTooltipOpen ? 'flex' : 'hidden group-hover:flex')}
                      onClick={onMarkAsRead}
                      aria-label="Mark as read"
                    >
                      <CheckIcon width={24} height={24} className="text-grey60" />
                    </IconButton>
                  </Tooltip>
                ))
                .with(
                  { isUnread: false, showUnreadToggle: true, onMarkAsUnread: P.not(P.nullish) },
                  ({ onMarkAsUnread }) => (
                    <Tooltip
                      title="Mark as unread"
                      placement="top"
                      open={isMarkAsUnreadTooltipOpen}
                      onOpen={() => setIsMarkAsUnreadTooltipOpen(true)}
                      onClose={() => setIsMarkAsUnreadTooltipOpen(false)}
                      slotProps={{ popper: { modifiers: [{ name: 'offset', options: { offset: [-32, -8] } }] } }}
                    >
                      <IconButton
                        size="small"
                        className={clsx(isMarkAsUnreadTooltipOpen ? 'flex' : 'hidden group-hover:flex')}
                        onClick={onMarkAsUnread}
                        aria-label="Mark as unread"
                      >
                        <MarkAsUnreadIcon width={24} height={24} className="text-grey60" />
                      </IconButton>
                    </Tooltip>
                  ),
                )
                .otherwise(() => null)}
            </Box>
          </>
        ))
        .otherwise(() => (
          <Box className="flex shrink-0 items-center justify-end" sx={{ minWidth: 80 }}>
            <Typography
              className={clsx('text-left text-xs text-grey60 group-hover:hidden', isMarkAsReadTooltipOpen && 'hidden')}
            >
              {formatNotificationDate(new Date(item.inserted_at))}
            </Typography>
            {isUnread && onMarkAsRead && (
              <Tooltip
                title="Mark as read"
                placement="top"
                open={isMarkAsReadTooltipOpen}
                onOpen={() => setIsMarkAsReadTooltipOpen(true)}
                onClose={() => setIsMarkAsReadTooltipOpen(false)}
                slotProps={{ popper: { modifiers: [{ name: 'offset', options: { offset: [-32, -8] } }] } }}
              >
                <IconButton
                  size="small"
                  className={clsx(isMarkAsReadTooltipOpen ? 'flex' : 'hidden group-hover:flex')}
                  onClick={onMarkAsRead}
                  aria-label="Mark as read"
                >
                  <CheckIcon width={24} height={24} className="text-grey60" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        ))}
    </Box>
  );
};

NotificationItem.displayName = 'NotificationItem';
